import { withQuery } from 'ufo'

export interface StatamicEntry {
    collection: {
        title: string
        handle: string
    }
    url: string
    title: string
    slug: string
    order?: number
}

export interface StatamicEntriesRequest {
    collection: string
    key?: string
    value?: string | number
    limit?: number
    filters?: [
        {
            key: string
            value: string | number
            operator?: string
        },
    ]
}

export const useStatamicEntries = () => {
    const statamic = useStatamic()

    const getEntries = async <T extends StatamicEntry>(
        data: StatamicEntriesRequest,
    ) => {
        let endpoint = `/collections/${data.collection}/entries`

        if (data.limit) {
            endpoint = withQuery(endpoint, { limit: data.limit })
        }

        if (data.filters) {
            data.filters.forEach((filter) => {
                const operator = filter.operator || 'equals'

                endpoint = withQuery(endpoint, {
                    [`filter[${filter.key}:${operator}]`]: filter.value,
                })
            })
        }

        return statamic<T[]>(endpoint, {
            transform: (data: any) => ({
                data: data.data.sort((a: T, b: T) =>
                    a.order && b.order
                        ? a.order - b.order
                        : a.title.localeCompare(b.title),
                ),
            }),
        })
    }

    const getEntryBy = async <T extends StatamicEntry>(
        data: StatamicEntriesRequest,
    ) => {
        const res = await statamic<T>(
            `/collections/${data.collection}/entries?filter[${data.key}:equals]=${data.value}`,
            {
                transform: (data: any) => data.data[0] ?? null,
            },
        )

        if (!res.data.value) {
            throw createError({
                statusCode: 404,
                statusMessage: 'Entry not found',
                fatal: true,
            })
        }

        return res
    }

    const getEntryById = <T extends StatamicEntry>(
        data: StatamicEntriesRequest,
    ) => getEntryBy<T>({ ...data, key: 'id' })

    const getEntryBySlug = <T extends StatamicEntry>(
        data: StatamicEntriesRequest,
    ) => getEntryBy<T>({ ...data, key: 'slug' })

    return {
        getEntries,
        getEntryBy,
        getEntryById,
        getEntryBySlug,
    }
}
